import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import { API } from '@/config/env'
import { ENV } from '@/config/env/constants'
import { useUserStore } from '@/store'
import { pinia } from '@/config/pinia'
import useNotifications from '@/store/notification'
import useLoading from '@/store/load'
import { getValidationError } from '../utils'
import { ErrorManager } from '../utils/errors/ErrorManager'
import { RequestManager } from './RequestManager'

let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (reason?: any) => void
}> = []
let isRefreshing = false

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

export class HttpService {
  private readonly _http: AxiosInstance

  constructor(baseUrlApi = API[ENV]) {
    this._http = axios.create({
      baseURL: baseUrlApi,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
    this.setInterceptors()
  }

  private setInterceptors() {
    const notificationStore = useNotifications(pinia)
    const loadingStore = useLoading(pinia)

    // request interceptor, add token to headers
    this._http.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const userStore = useUserStore(pinia)
        const token = await userStore.idToken

        const controller = new AbortController()
        const requestId = `${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`

        config.signal = controller.signal
        ;(config as any).requestId = requestId

        RequestManager.add(requestId, controller)

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor, handle errors and token refresh
    this._http.interceptors.response.use(
      (response) => {
        const config = response.config as any
        if (config && config.requestId) {
          RequestManager.remove(config.requestId)
        }
        return response
      },
      async (error) => {
        const userStore = useUserStore(pinia)
        const originalRequest = error.config

        if (originalRequest && originalRequest.requestId) {
          RequestManager.remove(originalRequest.requestId)
        }

        if (error.code === 'ERR_CANCELED') {
          loadingStore.dismissLoadingState()
          return Promise.reject(error)
        }

        if (error.response?.status === 403 && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject })
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = 'Bearer ' + token
                return this._http(originalRequest)
              })
              .catch((err) => Promise.reject(err))
          }

          originalRequest._retry = true
          isRefreshing = true

          try {
            const newIdToken = await userStore.refreshToken()
            if (newIdToken) {
              processQueue(null, newIdToken)
              originalRequest.headers['Authorization'] = `Bearer ${newIdToken}`
              return this._http(originalRequest)
            }
          } catch (error) {
            console.error('error:', error)
          } finally {
            isRefreshing = false
          }
        }

        const summary =
          getValidationError(error.response?.data?.code) ||
          error.response?.data?.message

        if (summary) {
          notificationStore.addErrorNotification({
            detail: `Status: ${error.response?.status}`,
            severity: error.response?.status >= 500 ? 'error' : 'info',
            summary,
          })
        }

        loadingStore.dismissLoadingState()
        ErrorManager.handleError(error)

        return Promise.reject(error)
      }
    )
  }

  async get<R>(pathUrl: string, opts?: AxiosRequestConfig) {
    return this._http.get<R>(pathUrl, opts)
  }

  async post<T, R>(pathUrl: string, data: T, opts?: AxiosRequestConfig) {
    return this._http.post<R>(pathUrl, data, opts)
  }

  async patch<T, R>(pathUrl: string, data?: T, opts?: AxiosRequestConfig) {
    return this._http.patch<R>(pathUrl, data, opts)
  }

  async put<T, R>(pathUrl: string, data: T, opts?: AxiosRequestConfig) {
    return this._http.put<R>(pathUrl, data, opts)
  }

  async delete<R>(pathUrl: string, opts?: AxiosRequestConfig) {
    return this._http.delete<R>(pathUrl, opts)
  }
}
